import React from 'react';
import { Calendar, Clock, Globe, Eye, Lock, FileText, AlertTriangle } from 'lucide-react';
import { WizardData } from '../components/PublishingWizard';
import CardComponent from '../ui/CardComponent';
import BadgeComponent from '../ui/BadgeComponent';

interface WizardStepFinalProps {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
}

const VISIBILITY_OPTIONS = [
  {
    id: 'public',
    name: 'Public',
    icon: Globe,
    description: 'Anyone can see your post',
  },
  {
    id: 'private',
    name: 'Private',
    icon: Lock,
    description: 'Only you can see this post',
  },
  {
    id: 'unlisted',
    name: 'Unlisted',
    icon: Eye,
    description: 'Anyone with the link can see this post',
  },
  {
    id: 'draft',
    name: 'Draft',
    icon: FileText,
    description: 'Save as draft for later',
  },
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
];

export default function WizardStepFinal({ data, updateData }: WizardStepFinalProps) {
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  const getPlatformWarnings = () => {
    const warnings = [];

    if (data.ageRestriction === '18+' && data.platforms.includes('Instagram')) {
      warnings.push({
        platform: 'Instagram',
        warning: 'Age-restricted content may have limited reach',
      });
    }

    if (data.platforms.includes('YouTube') && !data.subtitlesEnabled && data.mediaType === 'video') {
      warnings.push({
        platform: 'YouTube',
        warning: 'Subtitles are recommended for better accessibility',
      });
    }

    if (data.platforms.includes('TikTok') && data.mediaType !== 'video') {
      warnings.push({
        platform: 'TikTok',
        warning: 'TikTok only supports video content',
      });
    }

    return warnings;
  };

  const warnings = getPlatformWarnings();

  return (
    <div className="space-y-8">
      {/* Visibility */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-medium text-[#111827]">Visibility</h3>
          <p className="text-xs text-[#6B7280] mt-1">Choose who can see your post</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4">
            {VISIBILITY_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => updateData({ visibility: option.id })}
                  className={`p-4 border-2 rounded-lg transition-all text-left ${
                    data.visibility === option.id
                      ? 'border-[#2563EB] bg-[#EFF6FF] shadow-sm'
                      : 'border-[#E5E7EB] bg-white hover:border-[#9CA3AF] hover:bg-[#F9FAFB]'
                  }`}
                >
                  <Icon
                    size={24}
                    className={`mb-3 ${data.visibility === option.id ? 'text-[#2563EB]' : 'text-[#6B7280]'}`}
                  />
                  <div className="text-sm font-medium text-[#111827] mb-1">{option.name}</div>
                  <div className="text-xs text-[#6B7280]">{option.description}</div>
                </button>
              );
            })}
          </div>
        </div>
      </CardComponent>

      {/* Schedule Date & Time */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-medium text-[#111827]">Schedule Date & Time</h3>
          <p className="text-xs text-[#6B7280] mt-1">Choose when to publish your post</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#111827] mb-2">Publish Date *</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
              <input
                type="date"
                value={data.publishDate}
                onChange={(e) => updateData({ publishDate: e.target.value })}
                min={getTodayDate()}
                className="w-full h-10 pl-10 pr-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111827] mb-2">Publish Time *</label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
              <input
                type="time"
                value={data.publishTime}
                onChange={(e) => updateData({ publishTime: e.target.value })}
                className="w-full h-10 pl-10 pr-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111827] mb-2">Timezone</label>
            <select
              value={data.timezone}
              onChange={(e) => updateData({ timezone: e.target.value })}
              className="w-full h-10 px-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE] transition-all"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CardComponent>

      {/* Platform Warnings */}
      {warnings.length > 0 && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-yellow-600" />
              <h3 className="text-sm font-medium text-[#111827]">Platform Warnings</h3>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {warnings.map((warning, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle size={16} className="text-yellow-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-yellow-900">{warning.platform}</div>
                  <div className="text-xs text-yellow-700 mt-1">{warning.warning}</div>
                </div>
              </div>
            ))}
          </div>
        </CardComponent>
      )}

      {/* Publishing Summary */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-medium text-[#111827]">Publishing Summary</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">Post Title</span>
            <span className="text-sm font-medium text-[#111827]">{data.title || 'Untitled'}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">Platforms</span>
            <div className="flex gap-2">
              {data.platforms.map((platform) => (
                <BadgeComponent key={platform} variant="default">
                  {platform}
                </BadgeComponent>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">Media Type</span>
            <span className="text-sm font-medium text-[#111827] capitalize">{data.mediaType}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">Visibility</span>
            <span className="text-sm font-medium text-[#111827] capitalize">{data.visibility}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">Compliance Status</span>
            <BadgeComponent variant={data.complianceChecksComplete ? 'success' : 'warning'}>
              {data.complianceChecksComplete ? '✓ All checks passed' : '⚠ Pending'}
            </BadgeComponent>
          </div>

          {data.monetizationEnabled && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Monetization</span>
              <BadgeComponent variant="success">ON</BadgeComponent>
            </div>
          )}

          {(data.endScreenEnabled || data.cards.length > 0) && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Video Elements</span>
              <BadgeComponent variant="success">
                {data.endScreenEnabled && '✓ End Screen'}
                {data.cards.length > 0 && ` • ${data.cards.length} CardComponent${data.cards.length > 1 ? 's' : ''}`}
              </BadgeComponent>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6B7280]">SEO Elements</span>
            <span className="text-sm font-medium text-[#111827]">
              {data.hashtags.length} hashtags • {data.keywords.length} keywords
            </span>
          </div>

          {data.publishDate && data.publishTime && (
            <div className="pt-4 border-t border-[#E5E7EB]">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Scheduled for</span>
                <div className="text-right">
                  <div className="text-sm font-medium text-[#111827]">
                    {new Date(data.publishDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    {data.publishTime} ({data.timezone.split('/')[1].replace(/_/g, ' ')})
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardComponent>
    </div>
  );
}
